import { useEffect, useState } from "react";
import { Loader2, Sparkles, TrendingUp, Target, Lightbulb, CheckSquare } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import api from "../../../lib/api";

const verdictStyles = {
  normal: { bg: "bg-emerald-50", ring: "ring-emerald-200", fg: "text-emerald-800", label: "Subida normal" },
  moderada: { bg: "bg-amber-50", ring: "ring-amber-200", fg: "text-amber-800", label: "Subida moderada" },
  abusiva: { bg: "bg-red-50", ring: "ring-red-200", fg: "text-red-800", label: "Subida abusiva" },
};

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(n || 0);

export default function PriceHikeSuggestionDialog({
  open, onClose, restaurantId, ingredientId, ingredientName,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !ingredientId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    api.getPriceHikeSuggestion(restaurantId, ingredientId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => {
        if (!cancelled) setError(err.message || "No se pudo generar la sugerencia");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ingredientId, restaurantId]);

  const style = data ? (verdictStyles[data.verdict] || verdictStyles.moderada) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="price-hike-suggestion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Cotización alternativa IA
            <span className="text-xs font-normal text-muted-foreground ml-2">
              {ingredientName}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            <p className="text-sm text-muted-foreground">
              Consultando al asistente IA…<br />
              <span className="text-[11px]">Puede tardar hasta 30 segundos</span>
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-900 text-sm">
            {error}
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5 text-sm">
            {/* Verdict */}
            {style && (
              <div className={`rounded-lg ring-1 ${style.ring} ${style.bg} p-4 space-y-1`}>
                <div className="flex items-center gap-2">
                  <TrendingUp className={`h-4 w-4 ${style.fg}`} />
                  <span className={`font-semibold ${style.fg}`}>{style.label}</span>
                  {data.hike && (
                    <span className={`ml-auto tabular-nums font-bold ${style.fg}`}>
                      +{data.hike.pct_change?.toFixed(1)}%
                    </span>
                  )}
                </div>
                <p className={`text-xs ${style.fg} opacity-90`}>{data.verdict_reason}</p>
                {data.hike && (
                  <p className="text-[11px] text-muted-foreground">
                    {fmtEUR(data.hike.previous_price)} → <strong>{fmtEUR(data.hike.new_price)}</strong>
                    {data.hike.unit && <span>/{data.hike.unit}</span>}
                    {data.hike.supplier_name && <> · {data.hike.supplier_name}</>}
                  </p>
                )}
              </div>
            )}

            {/* Market range */}
            {data.market_range_eur && (
              <section className="space-y-2">
                <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                  <Target className="h-3.5 w-3.5" /> Rango de mercado en España
                </h4>
                <div className="rounded-md border p-3 bg-muted/50">
                  <div className="text-lg font-semibold tabular-nums">
                    {fmtEUR(data.market_range_eur.low)}
                    <span className="mx-2 text-muted-foreground text-sm">—</span>
                    {fmtEUR(data.market_range_eur.high)}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{data.market_range_eur.unit}
                    </span>
                  </div>
                  {data.market_range_eur.note && (
                    <p className="text-xs text-muted-foreground mt-1">{data.market_range_eur.note}</p>
                  )}
                </div>
              </section>
            )}

            {/* Alternative channels */}
            {Array.isArray(data.alternative_channels) && data.alternative_channels.length > 0 && (
              <section className="space-y-2">
                <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                  <Lightbulb className="h-3.5 w-3.5" /> Canales alternativos a explorar
                </h4>
                <ul className="divide-y rounded-md border">
                  {data.alternative_channels.map((c, i) => (
                    <li key={i} className="p-3 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{c.type}</span>
                        {c.typical_effort && (
                          <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-full ${
                            c.typical_effort === "bajo" ? "bg-emerald-100 text-emerald-700" :
                            c.typical_effort === "alto" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>
                            Esfuerzo {c.typical_effort}
                          </span>
                        )}
                      </div>
                      {c.pros && <p className="text-muted-foreground mt-1">{c.pros}</p>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Negotiation */}
            {Array.isArray(data.negotiation_points) && data.negotiation_points.length > 0 && (
              <section className="space-y-2">
                <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                  <CheckSquare className="h-3.5 w-3.5" /> Argumentos para renegociar
                </h4>
                <ul className="space-y-1.5 text-xs list-disc pl-5">
                  {data.negotiation_points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Action */}
            {data.action_this_week && (
              <section className="rounded-lg border-2 border-purple-200 bg-purple-50 p-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-purple-900 mb-1">
                      Acción priorizada esta semana
                    </h4>
                    <p className="text-xs text-purple-900">{data.action_this_week}</p>
                  </div>
                </div>
              </section>
            )}

            {data.cached && (
              <p className="text-[10px] text-right text-muted-foreground italic">
                ⚡ Respuesta cacheada (válida 7 días)
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end pt-3">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
